import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import SocialFooter from '../components/SocialFooter';
import { ArrowLeft, Warning, Trash, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') return;
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/account`, { withCredentials: true, headers });
      toast.success('Account deleted successfully');
      await logout();
      navigate('/welcome');
    } catch (err) {
      toast.error('Failed to delete account. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="delete-account-screen">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `url(${LOGO_URL})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundSize: '50%' }} />

      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <img src={LOGO_URL} alt="Track365" className="w-40 h-auto" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 relative z-10">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Trash weight="duotone" className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Delete Your Account
          </h1>
          <p className="text-muted-foreground">
            We're sorry to see you go. Please read carefully before proceeding.
          </p>
        </div>

        <div className="premium-card p-5 mb-6 border-red-500/20">
          <div className="flex items-start gap-3 mb-4">
            <Warning weight="fill" className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-400 font-semibold mb-2">This action is permanent and cannot be undone.</h3>
              <p className="text-sm text-muted-foreground">Deleting your account will:</p>
            </div>
          </div>
          <ul className="text-sm text-muted-foreground space-y-2 ml-8">
            <li>- Remove all your workout history and progress data</li>
            <li>- Delete your streak records and milestones</li>
            <li>- Cancel any active subscriptions</li>
            <li>- Remove your profile and personal information</li>
            <li>- This data cannot be recovered after deletion</li>
          </ul>
        </div>

        <div className="premium-card p-5 mb-6">
          <div className="flex items-start gap-3 mb-4">
            <ShieldCheck weight="duotone" className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-foreground font-semibold mb-1">Before you delete</h3>
              <p className="text-sm text-muted-foreground">
                If you're having issues, our support team can help. Contact us at{' '}
                <a href="mailto:support@track365cardio.com" className="text-[#D4AF37] hover:underline">
                  support@track365cardio.com
                </a>
              </p>
            </div>
          </div>
        </div>

        {user && (
          <div className="premium-card p-5">
            <p className="text-sm text-foreground mb-3">
              Type <span className="font-bold text-red-400">DELETE</span> to confirm account deletion:
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE here"
              className="w-full bg-card border border-border rounded-xl px-4 py-3 text-foreground mb-4 focus:outline-none focus:border-red-500"
              data-testid="delete-confirm-input"
            />
            <Button
              onClick={handleDelete}
              disabled={confirmText !== 'DELETE' || loading}
              className="w-full bg-red-500 text-white rounded-full py-4 font-semibold hover:bg-red-600 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="delete-account-btn"
            >
              {loading ? 'Deleting...' : 'Permanently Delete My Account'}
            </Button>
          </div>
        )}

        <SocialFooter />
      </main>
    </div>
  );
};

export default DeleteAccount;
